<script setup>
import GeneralLink from 'multisite/components/cmsContentBlocks/generalLink.vue';
import ThemedContainer from 'multisite/components/cmsContentBlocks/themedContainer.vue';
import { DsBadge, DsText } from '@coloplast/design-system';

defineProps({
	properties: {
		type: Object,
		default: null,
	},
});
const getDataCtaBehavior = (linkBehavior) => {
	return linkBehavior === 'RedirectToLink' ? 'redirect-to-link' : 'add-sample';
};
const getVariant = (linkBehavior) => {
	return linkBehavior === 'RedirectToLink' ? '' : 'primary';
};
</script>

<template>
	<ThemedContainer
		:html-element="'article'"
		data-testid="data-card-slider-marketing-card"
		class="c-card-slider-marketing-card"
		:background-color="properties.BackgroundColor"
	>
		<figure class="c-card-slider-marketing-card__figure">
			<img
				itemprop="image"
				:alt="properties.Header"
				:src="properties.ImageUrl"
				class="c-card-slider-marketing-card__figure__image"
				loading="lazy"
			/>
			<figcaption v-if="properties.BadgeText" class="c-card-slider-marketing-card__badge">
				<DsBadge :text="properties.BadgeText" />
			</figcaption>
		</figure>

		<div class="c-card-slider-marketing-card__info">
			<DsText
				class="c-card-slider-marketing-card__info__tag"
				html-element="span"
				text-style="regular"
				text-type="body-xs"
			>
				{{ properties.TagText }}
			</DsText>
			<div class="c-card-slider-marketing-card__info__header">
				<DsText html-element="span" text-style="bold" text-type="heading-md">
					{{ properties.Header }}
				</DsText>
			</div>
			<div class="c-card-slider-marketing-card__info__description">
				<DsText html-element="span" text-style="regular" text-type="body-sm">
					{{ properties.Description }}
				</DsText>
			</div>
		</div>
		<div class="c-card-slider-marketing-card__link">
			<GeneralLink
				:link-settings="{
					LinkBehavior: properties.LinkBehavior,
					AriaLabel: getDataCtaBehavior(properties.LinkBehavior),
					DisplayCartItem: properties.DisplayCartItem,
					LinkLabel: properties.Header,
					LinkTarget: properties.LinkTarget
				}"
				:url="properties.Url"
				:button-variant="getVariant(properties.LinkBehavior)"
				:variant="properties.AddItemToBasketRequest"
			>
			</GeneralLink>
		</div>
	</ThemedContainer>
</template>

<style lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.c-card-slider-marketing-card {
	overflow: hidden;
	position: relative;
	display: flex;
	flex-flow: column nowrap;
	justify-content: stretch;
	margin: 0;
	width: 100%;
	height: 100%;
	min-height: 350px;
	border-radius: 8px;
	border: 1px solid ds-color('border', 'muted');

	&__figure {
		align-items: center;
		display: flex;
		height: 100%;
		justify-content: center;
		margin: 0;
		min-height: 214px;
		overflow: hidden;
		position: relative;

		&__image {
			display: block;
			height: inherit;
			object-fit: cover;
			width: 100%;
		}
	}

	&__info {
		max-height: 136px;
		padding: 16px;

		&__tag,
		&__description {
			color: ds-color('fg', 'muted');
		}
	}

	&__badge {
		position: absolute;
		inset-block-start: 12px;
		inset-inline-end: 8px;
		margin: 0 ds-space(4) 0 ds-space(6);
	}

	&__link {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;

		a[aria-label='redirect-to-link'] {
			width: 100%;
			height: 100%;
			opacity: 0;

			& > span {
				position: absolute;
				overflow: hidden;
				clip: rect(0 0 0 0);
				height: 1px;
				width: 1px;
				margin: -1px;
				padding: 0;
				border: 0;
			}
		}

		button[aria-label='add-sample'] {
			width: 100%;
			height: 100%;
			opacity: 0;

			& > span {
				display: none;
			}
		}
	}
}
</style>
